import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { describeCallsError } from '../utils/apiErrorMessage';
import { useLocation } from '@/shared/hooks/useLocation';
import { callsApi } from '../api/callsApi';
import type { ClosureInput } from '../api/callsApi';
import { callsKeys } from '../queryKeys';

export function useCallClosures() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  const query = useQuery({
    queryKey: callsKeys.closures(locationId),
    queryFn: () => callsApi.listClosures(locationId),
    enabled: !!locationId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: callsKeys.closures(locationId) });

  const create = useMutation({
    mutationFn: (input: Omit<ClosureInput, 'locationId'>) =>
      callsApi.createClosure({ ...input, locationId }),
    onSuccess: () => {
      toast.success('Closure scheduled — agents will use it automatically');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to schedule closure')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => callsApi.deleteClosure(id, locationId),
    onSuccess: () => {
      toast.success('Closure removed');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to remove closure')),
  });

  return {
    closures: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    create,
    remove,
  };
}
